import React from 'react';
import { useSelector } from 'react-redux';

import { makeStyles } from 'tss-react/mui';
import range from 'lodash-es/range';

import { useIntl } from 'ha/i18n';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { getSelectedVariants } from 'ha/modules/Experiments/selectors';
import { Experiment } from 'ha/modules/Experiments';
import { Experiments as EXPERIMENTS } from 'ha/constants/experiments';

import { ListingPhoto } from '@ha/api/v2/types/Listing';
import { Grid } from '@hbf/dsl';

import {
  BoxCorner3D,
  CameraSettingsRotate1Bold,
  ModernTV3DSyncBold,
  RealEstateDimensionsBlockBold,
  RealEstateDimensionsPlan,
  VideoPlayer,
  VideoGame360,
  VideoPlayerSliderBold,
} from '@ha/icons';

import {
  PreviewTile,
  PreviewTileProps,
} from 'ha/components/Redesign/PreviewTile';
import NoImageTileSrc from 'ha/img-src/listing/no-img-tile.jpg';

interface Props {
  country: string;
  onPanoramicPhotosButtonClick: () => void;
  onVideoButtonClick: () => void;
  onFloorplanButtonClick: () => void;
  onVirtualTourButtonClick: () => void;
  onImageClick: (id: number) => void;
  numVideos: number;
  photos: ListingPhoto[];
  floorplanPhotos: ListingPhoto[];
  panoramicPhotos: ListingPhoto[];
  virtualTourLink?: string;
}

const useStyles = makeStyles()(() => ({
  tile: {
    width: '16.66%',
  },
  tileButton: {
    height: '79px',
  },
}));

export const GalleryDesktopTiles: React.FC<Props> = ({
  country,
  onPanoramicPhotosButtonClick,
  onVideoButtonClick,
  onFloorplanButtonClick,
  onVirtualTourButtonClick,
  photos,
  floorplanPhotos,
  panoramicPhotos,
  numVideos,
  virtualTourLink,
  onImageClick,
}) => {
  const { classes } = useStyles();
  const { T } = useIntl();
  const { rearrangeMediaGalleryLDP } = useFeatureFlags();

  const [hasMedia, setHasMedia] = React.useState(false);

  const getGalleryTilesExperiment =
    useSelector(getSelectedVariants)[EXPERIMENTS.TX1249.name];

  const isExperimentEnabled = React.useMemo(() => {
    return (
      hasMedia && country !== 'Netherlands' && rearrangeMediaGalleryLDP === 'on'
    );
  }, [country, hasMedia, rearrangeMediaGalleryLDP]);

  const isNewLayout =
    isExperimentEnabled &&
    (getGalleryTilesExperiment === 'C' || getGalleryTilesExperiment === 'D');

  /*
    - always show the tiles for non-photos type of content (since they already can be opened by clicking on gallery)
    - if there's enough space left, add "More photos" tile (if listing has any additional photos other than photo tiles and gallery photo)
    - if there's enough space left, add photo tiles to the front until the line is full (if listing has enough photos)
  */
  const displayedTiles = React.useMemo(() => {
    const imageTiles: Array<PreviewTileProps> = [];
    const showMoreTile: Array<PreviewTileProps> = [];
    const multimediaTiles: Array<PreviewTileProps> = [];

    let hasMediaTiles = false;

    const maxNumberOfTiles = 6;

    let experimentType: PreviewTileProps['type'] = 'top';

    if (isExperimentEnabled && getGalleryTilesExperiment === 'C') {
      experimentType = 'dark';
    } else if (isExperimentEnabled && getGalleryTilesExperiment === 'D') {
      experimentType = 'light';
    }

    if (numVideos > 0) {
      hasMediaTiles = true;
      multimediaTiles.push({
        dataTestLocator: 'Video button',
        type: experimentType,
        icon: isNewLayout ? (
          <VideoPlayer fontSize="inherit" />
        ) : (
          <VideoPlayerSliderBold fontSize="inherit" />
        ),
        title: T('listings.media.video'),
        onClick: onVideoButtonClick,
        backgroundImage: photos[0]?.url || NoImageTileSrc,
      });
    }
    if (floorplanPhotos.length > 0) {
      hasMediaTiles = true;
      multimediaTiles.push({
        dataTestLocator: 'Floor Plan button',
        type: experimentType,
        icon: isNewLayout ? (
          <RealEstateDimensionsPlan fontSize="inherit" />
        ) : (
          <RealEstateDimensionsBlockBold fontSize="inherit" />
        ),
        title: T('listings.media.floorplan'),
        onClick: onFloorplanButtonClick,
        backgroundImage: floorplanPhotos?.[0].url,
      });
    }
    if (panoramicPhotos.length > 0) {
      hasMediaTiles = true;
      multimediaTiles.push({
        dataTestLocator: 'Panoramic photo button',
        type: experimentType,
        icon: isNewLayout ? (
          <VideoGame360 fontSize="inherit" />
        ) : (
          <CameraSettingsRotate1Bold fontSize="inherit" />
        ),
        title: '360°',
        onClick: onPanoramicPhotosButtonClick,
        backgroundImage: panoramicPhotos?.[0].url,
      });
    }

    if (virtualTourLink) {
      hasMediaTiles = true;
      multimediaTiles.push({
        dataTestLocator: 'Virtual tour button',
        type: experimentType,
        icon: isNewLayout ? (
          <BoxCorner3D fontSize="inherit" />
        ) : (
          <ModernTV3DSyncBold fontSize="inherit" />
        ),
        title: T('listing_gallery_3D_tour'),
        onClick: onVirtualTourButtonClick,
        backgroundImage: NoImageTileSrc,
      });
    }

    const additionalTiles = Math.min(
      maxNumberOfTiles - multimediaTiles.length,
      photos.length - 1,
    );

    const hasMorePhotos = photos.length - 1 > additionalTiles; //has more photos than tile

    // number of spaces available to photos
    // if has more photos to display then reserve 1 tile for showMore button
    // otherwise, just show all remaining photos
    const photosToAdd = hasMorePhotos ? additionalTiles - 1 : additionalTiles;

    const showMoreTileTitle = isNewLayout
      ? T('listings.media.more_photos')
      : T('listings.media.more_photos.%d', photos.length - additionalTiles);

    const showMoreTileIcon = isNewLayout && photos.length - additionalTiles;

    if (photosToAdd > 0) {
      imageTiles.push(
        ...range(0, photosToAdd).map(index => ({
          dataTestLocator: `Photo button ${index}`,
          onClick: () => onImageClick(index + 1),
          backgroundImage: photos[index + 1]?.url,
        })),
      );
    }

    if (hasMorePhotos) {
      hasMediaTiles = true;
      showMoreTile.push({
        dataTestLocator: 'More photo button',
        type: !isNewLayout ? ('centralized' as const) : experimentType,
        title: showMoreTileTitle,
        icon: showMoreTileIcon,
        onClick: () => onImageClick(imageTiles.length + 1),
        backgroundImage: photos[imageTiles.length + 1]?.url,
      });
    }

    setHasMedia(hasMediaTiles);

    if (
      getGalleryTilesExperiment !== 'A' &&
      rearrangeMediaGalleryLDP === 'on'
    ) {
      return [...imageTiles, ...showMoreTile, ...multimediaTiles];
    }

    return [...imageTiles, ...multimediaTiles, ...showMoreTile];
  }, [
    isExperimentEnabled,
    getGalleryTilesExperiment,
    numVideos,
    floorplanPhotos,
    panoramicPhotos,
    virtualTourLink,
    photos,
    T,
    onVideoButtonClick,
    onFloorplanButtonClick,
    onPanoramicPhotosButtonClick,
    onVirtualTourButtonClick,
    onImageClick,
    isNewLayout,
  ]);

  return (
    <Grid container spacing={1}>
      {displayedTiles.map((tile, index) => (
        <Grid
          item
          key={tile.dataTestLocator ? tile.dataTestLocator : index}
          className={classes.tile}
        >
          {isExperimentEnabled && <Experiment name={EXPERIMENTS.TX1249.name} />}
          <PreviewTile
            {...tile}
            className={classes.tileButton}
            onClick={tile.onClick}
            backgroundImage={tile.backgroundImage || NoImageTileSrc}
          />
        </Grid>
      ))}
    </Grid>
  );
};
